/**
 * Loading, empty, failure.
 *
 * Thin wrappers over the app's existing Spinner / Empty / ErrorNotice, under
 * the names the audit workspace uses, so every screen reaches for the same
 * three states and none of them ever renders a blank panel or a raw error.
 */

import type { ReactNode } from 'react';
import { Card, Empty, ErrorNotice, Spinner } from '../../shared/ui';

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
	return (
		<Card>
			<Spinner label={label} />
		</Card>
	);
}

export function EmptyState({
	title,
	children,
	action,
	icon,
}: {
	title: string;
	children?: ReactNode;
	action?: ReactNode;
	icon?: ReactNode;
}) {
	return (
		<Card>
			<Empty title={title} action={action} icon={icon}>
				{children}
			</Empty>
		</Card>
	);
}

/**
 * A failure, in a card rather than loose on the page.
 *
 * `hint` is where a screen says what to do about it. An error with no next
 * step reads as a broken build rather than as a designed state.
 */
export function ErrorState({
	error,
	onRetry,
	hint,
}: {
	error: unknown;
	onRetry?: () => void;
	hint?: ReactNode;
}) {
	if (!error) return null;
	return (
		<Card>
			<ErrorNotice error={error} onRetry={onRetry} />
			{hint && <div className="mt3 small mut">{hint}</div>}
		</Card>
	);
}
